import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useInstantAccountValidation = (botId?: string) => {
  const { user } = useAuth();

  // Check if the bot is an instant account bot
  const { data: botDetails } = useQuery({
    queryKey: ['propBotInstantCheck', botId],
    queryFn: async () => {
      if (!botId) return null;
      
      const { data, error } = await supabase
        .from('bot_prop_details')
        .select('is_instant_account')
        .eq('bot_id', botId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!botId,
  });

  // Get user's trading accounts
  const { data: tradingAccounts } = useQuery({
    queryKey: ['userTradingAccounts', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('trading_accounts')
        .select('*, api_connections!inner(csp_accounts!inner(user_id))')
        .eq('api_connections.csp_accounts.user_id', user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const canConnectAccount = (accountId: string, isLive: boolean) => {
    if (!botDetails?.is_instant_account) {
      // Regular prop bot - can connect any account
      return true;
    }

    // Instant account prop bot - only allow live accounts
    return isLive;
  };

  const getAccountValidationError = (isLive: boolean) => {
    if (!botDetails?.is_instant_account) {
      return null;
    }

    if (!isLive) {
      return 'This is an Instant Account prop bot and only accepts live trading accounts. Demo accounts cannot be connected to this bot.';
    }

    return null;
  };

  const isInstantAccountBot = botDetails?.is_instant_account || false;
  const availableLiveAccounts = tradingAccounts?.filter(account => account.live) || [];
  const availableDemoAccounts = tradingAccounts?.filter(account => !account.live) || [];

  return {
    canConnectAccount,
    getAccountValidationError,
    isInstantAccountBot,
    availableLiveAccounts,
    availableDemoAccounts,
    totalAccounts: tradingAccounts?.length || 0,
  };
};